import React, { useState, useEffect } from 'react';
import { Calendar, Info } from 'lucide-react';
import AgendaCard from './AgendaCard';
import SummaryDashboard from '../common/SummaryDashboard';
import LoadingState from '../common/LoadingState';
import { buildApiUrl } from '../../../../services/apiConfig';

const IntelligentAgendasTab = ({ clientBehavioralData, selectedClient }) => {
  const [expandedAgenda, setExpandedAgenda] = useState(null);
  const [meetingAgendas, setMeetingAgendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [triggers, setTriggers] = useState([]);

  const toggleAgenda = (id) => {
    if (expandedAgenda === id) {
      setExpandedAgenda(null);
    } else {
      setExpandedAgenda(id);
    }
  };

  // Helper function to extract JSON from markdown code blocks
  const extractJsonFromMarkdown = (text) => {
    if (text.includes('```json')) {
      const match = text.match(/```json\s*([\s\S]*?)\s*```/);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    return text;
  };

  // Get triggers from sessionStorage
  const fetchTriggers = () => {
    if (!selectedClient) return [];
    
    // Use the same page load key as in other tabs
    const pageLoadKey = sessionStorage.getItem('page_load_key');
    
    // Check if we have cached triggers
    const cachedTriggersKey = `triggers_${selectedClient.id}_${pageLoadKey}`;
    
    const cachedTriggers = sessionStorage.getItem(cachedTriggersKey);
    if (cachedTriggers) {
      return JSON.parse(cachedTriggers);
    }
    
    return [];
  };

  const fetchAgendas = async () => {
    if (!selectedClient) return;
    
    // Use the same page load key as in other tabs
    const pageLoadKey = sessionStorage.getItem('page_load_key');
    
    // Check if we have cached agendas for this client and this page load
    const cachedAgendasKey = `agendas_${selectedClient.id}_${pageLoadKey}`;
    const cachedAgendas = sessionStorage.getItem(cachedAgendasKey);
    
    if (cachedAgendas) {
      setMeetingAgendas(JSON.parse(cachedAgendas));
      return;
    }
    
    setLoading(true);
    try {
      // Get triggers first
      const fetchedTriggers = fetchTriggers();
      setTriggers(fetchedTriggers);
      
      // Create a FormData object
      const formData = new FormData();

      // Create a JSON string for the client data with triggers
      const clientData = JSON.stringify({
        "client": {
          "id": selectedClient.id,
          "name": selectedClient.name,
          "age": selectedClient.age || 58,
          "occupation": selectedClient.occupation || "Retired",
          "annual_income": selectedClient.annual_income || 250000,
          "net_worth": selectedClient.net_worth || 12800000,
          "risk_profile": selectedClient.risk_profile || "Moderate",
          "investment_horizon": selectedClient.investment_horizon || "5-10 years",
          "portfolio": selectedClient.portfolio || {
            "total_value": 9100000,
            "allocation": {
              "equities": 65,
              "fixed_income": 20,
              "alternatives": 10,
              "cash": 5
            }
          },
          "goals": selectedClient.goals || [
            "Retirement income planning",
            "Estate planning",
            "Philanthropic initiatives"
          ]
        },
        "triggers": fetchedTriggers,
        "metadata": {
          "timestamp": new Date().toISOString()
        }
      });

      // Create a Blob from the JSON string
      const fileBlob = new Blob([clientData], { type: 'application/json' });

      // Append the file to the FormData
      formData.append('file', fileBlob, 'client-data.json');

      // Append the prompt to the FormData
      formData.append('prompt', promptText);

      // Make the API call
      const response = await fetch(buildApiUrl('JAMBA', '/custom-analyze'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();

      // Extract JSON from markdown if needed
      const cleanJsonString = extractJsonFromMarkdown(result.data);

      // Parse the JSON string
      const parsedAgendas = JSON.parse(cleanJsonString);

      // Save to state and sessionStorage
      setMeetingAgendas(parsedAgendas);
      sessionStorage.setItem(cachedAgendasKey, JSON.stringify(parsedAgendas));
      
    } catch (err) {
      console.error('Error fetching agendas:', err);
      setError(err.message);
      
      // If there's an error, provide some default agendas
      const defaultAgendas = [
        {
          "id": "agenda_default1",
          "title": "Quarterly Portfolio Review",
          "status": "Draft",
          "date": "Next available date",
          "duration": "60 minutes",
          "format": "Video Conference",
          "generatedFrom": ["Client Goals", "Market Events"],
          "items": [
            {
              "title": "Portfolio Performance Review",
              "description": "Review current portfolio performance against benchmarks and goals",
              "duration": "20 minutes",
              "priority": "High",
              "relatedTriggers": []
            },
            {
              "title": "Market Outlook Discussion",
              "description": "Discuss current market conditions and outlook for the next quarter",
              "duration": "15 minutes",
              "priority": "Medium",
              "relatedTriggers": []
            },
            {
              "title": "Goal Progress Assessment",
              "description": "Evaluate progress toward financial goals and make adjustments as needed",
              "duration": "15 minutes",
              "priority": "High",
              "relatedTriggers": []
            },
            {
              "title": "Next Steps and Action Items",
              "description": "Summarize action items and set expectations for next meeting",
              "duration": "10 minutes",
              "priority": "Medium",
              "relatedTriggers": []
            }
          ],
          "preparationNotes": [
            "Prepare portfolio performance report",
            "Review client's financial goals and progress",
            "Research recent market developments"
          ],
          "clientPreferences": {
            "communicationStyle": "Detailed",
            "meetingPace": "Methodical",
            "decisionMaking": "Data-driven"
          }
        }
      ];
      
      setMeetingAgendas(defaultAgendas);
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Generate a unique page load key when the component mounts if it doesn't exist
    if (!sessionStorage.getItem('page_load_key')) {
      sessionStorage.setItem('page_load_key', Date.now().toString());
    }
    
    if (selectedClient) {
      fetchAgendas();
    }
  }, [selectedClient]);

  const promptText = `Based on the provided client data and triggers, generate intelligent meeting agendas for a financial advisor.

Create 2-3 different meeting agenda options with varying focuses (e.g., quarterly review, specific issue discussion, comprehensive planning).

Format the response as a JSON array with the following structure for each agenda:
[
  {
    "id": "agenda_" + [random 8-character alphanumeric string],
    "title": "Clear descriptive title for the meeting",
    "status": "Draft | Ready | Completed",
    "date": "Next available date in format: Month DD, YYYY",
    "duration": "60 minutes | 90 minutes | 45 minutes",
    "format": "In-person | Video Conference | Phone Call",
    "generatedFrom": [
      "Active Triggers",
      "Recommended Conversations",
      "Client Goals",
      "Market Events",
      "Regulatory Changes"
    ],
    "items": [
      {
        "title": "Brief title of agenda item",
        "description": "Detailed description of what to discuss",
        "duration": "15 minutes | 10 minutes | 20 minutes",
        "priority": "High | Medium | Low",
        "relatedTriggers": ["trig_id1", "trig_id2"] // IDs of related triggers if applicable
      }
    ],
    "preparationNotes": [
      "Specific preparation task for the advisor",
      "Another preparation task",
      "Documents to gather before the meeting"
    ],
    "clientPreferences": {
      "communicationStyle": "Direct | Visual | Detailed | Concise",
      "meetingPace": "Brisk | Methodical | Flexible",
      "decisionMaking": "Data-driven | Relationship-focused | Cautious | Decisive"
    }
  }
]

Ensure that:
1. Each agenda incorporates high-priority triggers
2. Time allocation is realistic and appropriate for the meeting duration
3. Items are sequenced logically with related topics grouped together
4. The total duration of all agenda items matches the overall meeting duration
5. Client preferences are considered in the agenda structure
6. Preparation notes are specific and actionable

If there are high-priority triggers, ensure they are included in at least one agenda option.
If no triggers are provided, create general agenda templates based on the client's profile and goals.

DO NOT return any additional text.`;

  if (loading) {
    return <LoadingState message="Loading intelligent agendas..." />;
  }

  if (error && (!meetingAgendas || meetingAgendas.length === 0)) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <p className="text-red-500">Error loading meeting agendas: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          Builds intelligent meeting agendas optimized for time allocation and topic prioritization.
        </p>
        <button
          onClick={() => setShowPrompt(!showPrompt)}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          title="Show prompt information"
        >
          <Info size={18} className="text-gray-500" />
        </button>
      </div>

      {showPrompt && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Prompt:</h4>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap">{promptText}</pre>
        </div>
      )}

      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="bg-purple-100 rounded-full p-2 mr-4">
            <Calendar size={20} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-800 mb-1">Intelligent Agenda Builder</h3>
            <p className="text-sm text-purple-700">
              Automatically generated meeting agendas based on active triggers, client preferences, and upcoming milestones. Each agenda is optimized for time allocation and prioritizes the most important topics.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-gray-800">Upcoming Meeting Agendas</h3>
      </div>

      {meetingAgendas.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No meeting agendas found for this client.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {meetingAgendas.map((agenda) => (
            <AgendaCard
              key={agenda.id}
              agenda={agenda}
              isExpanded={expandedAgenda === agenda.id}
              onToggle={toggleAgenda}
              triggers={triggers}
            />
          ))}
        </div>
      )}

    </div>
  );
};

export default IntelligentAgendasTab;